/**
 * Academic Performance Data Utilities
 * 
 * Provides helper functions for formatting and displaying academic performance 
 * metrics from the school_academics table. Supports multiple metric types:
 * - Letter grades (TX: A-F)
 * - Proficiency rates (IL: subject-specific percentages)
 * - Performance levels (CA: color ratings)
 */

// Standard metric categories in display order
export const ACADEMIC_CATEGORIES = [
  'overall', 
  'school_progress', 
  'student_achievement', 
  'closing_gaps',
  'math', 
  'reading',
  'ela',  // English Language Arts (Indiana, some other states)
  'science', 
  'writing'
];

// Display labels for metric categories (used in popups)
export const ACADEMIC_CATEGORY_LABELS = {
  overall: 'Academic Rating',
  school_progress: 'School Progress',
  student_achievement: 'Student Achievement',
  closing_gaps: 'Closing Gaps',
  math: 'Math',
  reading: 'Reading',
  ela: 'ELA',
  science: 'Science',
  writing: 'Writing'
};

// Shortened labels for table headers
export const ACADEMIC_CATEGORY_SHORT_LABELS = {
  overall: 'Academic Rating',
  school_progress: 'School Progress',
  student_achievement: 'Student Achievement',
  closing_gaps: 'Closing Gaps',
  math: 'Math Proficiency',
  reading: 'Reading Proficiency',
  ela: 'ELA Proficiency',
  science: 'Science Proficiency',
  writing: 'Writing Proficiency'
};

/**
 * Format a category name for display
 * Handles known labels and formats unknown ones by replacing underscores with spaces
 * @param {string} category - The category key (e.g., 'school_progress')
 * @returns {string} Formatted display name (e.g., 'School Progress')
 */
export const formatCategoryLabel = (category) => {
  // Check if we have a predefined label
  if (ACADEMIC_CATEGORY_LABELS[category]) {
    return ACADEMIC_CATEGORY_LABELS[category];
  }
  
  // Otherwise, format the category name by replacing underscores and capitalizing
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format an academic value for display based on its metric type
 * @param {Object} metricData - The metric data object with value, numeric_value, and metric_type
 * @returns {string} Formatted value for display
 */
export const formatAcademicValue = (metricData) => {
  if (!metricData) return 'N/A';
  
  const { value, numeric_value, metric_type } = metricData;
  
  // Handle case where value is "NaN" string or invalid - fall back to numeric_value
  if (!value || value === 'NaN' || value === 'null' || value === 'undefined') {
    if (numeric_value !== null && numeric_value !== undefined) {
      if (metric_type === 'proficiency_rate') {
        // numeric_value is stored as decimal (0.0-1.0), convert to percentage
        const numericVal = typeof numeric_value === 'string' ? parseFloat(numeric_value) : numeric_value;
        const percentage = Math.round(numericVal * 100);
        return `${percentage}%`;
      }
      // For other types, return the numeric value as-is
      return String(numeric_value);
    }
    return 'N/A';
  }
  
  switch (metric_type) {
    case 'letter_grade':
      // Return letter grade as-is (e.g., "A", "B+")
      return value;
    case 'proficiency_rate': {
      // Handle proficiency rates - could be percentage (>=1) or decimal (0-1)
      const numericVal = typeof value === 'string' ? parseFloat(value) : value;
      
      // If value is a decimal (0-1), convert to percentage
      if (!isNaN(numericVal) && numericVal > 0 && numericVal <= 1) {
        const percentage = Math.round(numericVal * 100);
        return `${percentage}%`;
      }
      
      // If already a percentage (1-100), format with %
      if (!isNaN(numericVal) && numericVal > 1 && numericVal <= 100) {
        return `${Math.round(numericVal)}%`;
      }
      
      // If value already has % symbol, return as-is
      if (typeof value === 'string' && value.includes('%')) {
        return value;
      }
      
      // Fallback to numeric_value if available
      if (numeric_value !== null && numeric_value !== undefined) {
        const fallbackVal = typeof numeric_value === 'string' ? parseFloat(numeric_value) : numeric_value;
        const percentage = Math.round(fallbackVal * 100);
        return `${percentage}%`;
      }
      
      return 'N/A';
    }
    case 'performance_level':
      // Capitalize performance level
      return value.charAt(0).toUpperCase() + value.slice(1);
    default:
      return value;
  }
};

/**
 * Get the numeric value for sorting from a metric
 * @param {Object} metricData - The metric data object
 * @returns {number} Numeric value for sorting (higher is better)
 */
export const getAcademicNumericValue = (metricData) => {
  if (!metricData) return -1;
  if (metricData.numeric_value !== null && metricData.numeric_value !== undefined) {
    return metricData.numeric_value;
  }
  return -1;
};

/**
 * Get an academic value for a specific category from a school's data
 * @param {Object} schoolData - The school's enrollment/academic data
 * @param {string} category - The metric category (e.g., 'overall', 'math')
 * @returns {Object|null} The metric data object or null
 */
export const getAcademicMetric = (schoolData, category) => {
  if (!schoolData?.academics || !schoolData.academics[category]) {
    return null;
  }
  return schoolData.academics[category];
};

/**
 * Check if any school in the list has academic data
 * @param {Array} schoolsData - Array of school data objects
 * @returns {boolean} True if at least one school has academic data
 */
export const hasAcademicData = (schoolsData) => {
  if (!schoolsData || !Array.isArray(schoolsData)) return false;
  return schoolsData.some(school => school?.academics && Object.keys(school.academics).length > 0);
};

/**
 * Get all available academic categories across a list of schools
 * Returns only categories that have data for at least one school
 * Flexible: includes both predefined categories and any others found in the data
 * @param {Array} schoolsData - Array of school data objects
 * @returns {Array} List of category keys that have data
 */
export const getAvailableAcademicCategories = (schoolsData) => {
  if (!schoolsData || !Array.isArray(schoolsData)) return [];
  
  const availableCategories = new Set();
  
  schoolsData.forEach(school => {
    if (school?.academics) {
      Object.keys(school.academics).forEach(category => {
        availableCategories.add(category);
      });
    }
  });
  
  // Convert to array
  const categoriesArray = Array.from(availableCategories);
  
  // Sort: predefined categories first (in standard order), then others alphabetically
  return categoriesArray.sort((a, b) => {
    const indexA = ACADEMIC_CATEGORIES.indexOf(a);
    const indexB = ACADEMIC_CATEGORIES.indexOf(b);
    
    // If both are in predefined list, use their order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in predefined list, it comes first
    if (indexA !== -1) return -1;
    // If only B is in predefined list, it comes first
    if (indexB !== -1) return 1;
    // Both are not in predefined list, sort alphabetically
    return a.localeCompare(b);
  });
};

/**
 * Get color class/style for an academic value based on metric type
 * @param {Object} metricData - The metric data object
 * @returns {Object} Style object with color property
 */
export const getAcademicColorStyle = (metricData) => {
  if (!metricData) return { color: 'text.secondary' };
  
  const { value, metric_type, numeric_value } = metricData;
  
  if (metric_type === 'letter_grade') {
    // Color coding for letter grades
    const letter = value?.charAt(0)?.toUpperCase();
    switch (letter) {
      case 'A':
        return { color: 'success.main', fontWeight: 600 };
      case 'B':
        return { color: 'success.light', fontWeight: 500 };
      case 'C':
        return { color: 'warning.main', fontWeight: 500 };
      case 'D':
        return { color: 'error.light', fontWeight: 500 };
      case 'F':
        return { color: 'error.main', fontWeight: 600 };
      default:
        return { color: 'text.primary' };
    }
  } else if (metric_type === 'proficiency_rate' && numeric_value !== null) {
    // Color coding for proficiency rates
    // Handle both formats: percentage (0-100) or decimal (0-1)
    const normalizedValue = numeric_value > 1 ? numeric_value : numeric_value * 100;
    
    if (normalizedValue >= 80) {
      return { color: 'success.main', fontWeight: 600 };
    } else if (normalizedValue >= 60) {
      return { color: 'success.light', fontWeight: 500 };
    } else if (normalizedValue >= 40) {
      return { color: 'warning.main', fontWeight: 500 };
    } else if (normalizedValue >= 20) {
      return { color: 'error.light', fontWeight: 500 };
    } else {
      return { color: 'error.main', fontWeight: 600 };
    }
  } else if (metric_type === 'performance_level') {
    // Color coding for performance levels (e.g., California's color system)
    const lowerValue = value?.toLowerCase();
    switch (lowerValue) {
      case 'blue':
      case 'green':
        return { color: 'success.main', fontWeight: 600 };
      case 'yellow':
        return { color: 'warning.main', fontWeight: 500 };
      case 'orange':
        return { color: 'error.light', fontWeight: 500 };
      case 'red':
        return { color: 'error.main', fontWeight: 600 };
      default:
        return { color: 'text.primary' };
    }
  }
  
  return { color: 'text.primary' };
};

/**
 * Format academic data for CSV export
 * @param {Object} academics - The academics object from school data
 * @param {string} category - The category to format
 * @returns {string} Formatted value for CSV
 */
export const formatAcademicForCSV = (academics, category) => {
  if (!academics || !academics[category]) return 'N/A';
  return formatAcademicValue(academics[category]);
};

/**
 * Get a compact summary of academic metrics for popup display
 * @param {Object} academics - The academics object from school data
 * @returns {string} Compact summary string (e.g., "Overall: A | Math: 82%")
 */
export const getAcademicSummary = (academics) => {
  if (!academics || Object.keys(academics).length === 0) return null;
  
  const summaryParts = [];
  
  // Prioritize overall, then math, then reading
  const priorityCategories = ['overall', 'math', 'reading'];
  
  for (const category of priorityCategories) {
    if (academics[category]) {
      const label = ACADEMIC_CATEGORY_LABELS[category];
      const value = formatAcademicValue(academics[category]);
      summaryParts.push(`${label}: ${value}`);
    }
    // Limit to 3 metrics for compact display
    if (summaryParts.length >= 3) break;
  }
  
  return summaryParts.length > 0 ? summaryParts.join(' | ') : null;
};

/**
 * Compare academic numeric values for sorting
 * @param {Object} a - First school data
 * @param {Object} b - Second school data  
 * @param {string} category - Category to compare
 * @returns {number} Comparison result for sorting
 */
export const compareAcademicValues = (a, b, category) => {
  const valueA = getAcademicNumericValue(getAcademicMetric(a, category));
  const valueB = getAcademicNumericValue(getAcademicMetric(b, category));
  return valueA - valueB;
};

